import React from 'react';

import { TextField, TextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldError,
  RegisterOptions,
} from 'react-hook-form';

import colors from '../../theme/colors';

interface InputElementProps {
  name: string;
  control: Control<any>; // Type for React Hook Form's control prop
  rules?: RegisterOptions; // Type for rules (validation options)
  required?: boolean;
  type?: string;
}

// Beautiful styled TextField component
const BeautifulTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: `1px solid rgba(18, 112, 220, 0.15)`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    paddingTop: '8px', // Add top padding to prevent label clipping

    '&:hover': {
      borderColor: colors.primary,
      boxShadow: `0 2px 8px rgba(18, 112, 220, 0.12)`,
    },

    '&.Mui-focused': {
      borderColor: colors.primary,
      boxShadow: `0 4px 12px rgba(18, 112, 220, 0.2)`,
    },

    '&.Mui-error': {
      borderColor: colors.errorMain,
      boxShadow: `0 2px 8px rgba(255, 99, 71, 0.15)`,

      '&:hover': {
        borderColor: colors.errorMain,
        boxShadow: `0 4px 12px rgba(255, 99, 71, 0.2)`,
      },
    },

    '& .MuiOutlinedInput-input': {
      padding: '6px 14px',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#2d3748',
      transition: 'all 0.3s ease',

      '&::placeholder': {
        color: '#a0aec0',
        opacity: 1,
        fontWeight: 400,
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },

  '& .MuiInputLabel-root': {
    color: '#718096',
    fontWeight: 500,
    fontSize: '0.85rem',
    transition: 'all 0.3s ease',
    backgroundColor: 'rgba(255, 255, 255, 0.95)', // Add background to prevent border clipping
    padding: '0 4px', // Add padding around label text

    '&.Mui-focused': {
      color: colors.primary,
      fontWeight: 600,
    },

    '&.Mui-error': {
      color: colors.errorMain,
    },

    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)', // Ensure background on shrunk label
    },
  },

  '& .MuiFormHelperText-root': {
    margin: '4px 0 0 4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    transition: 'all 0.3s ease',

    '&.Mui-error': {
      color: colors.errorMain,
      animation: 'shake 0.5s ease-in-out',
    },
  },

  // Add subtle animation for error states
  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '25%': { transform: 'translateX(-2px)' },
    '75%': { transform: 'translateX(2px)' },
  },
}));

// Use `TextFieldProps` directly when spreading into TextField
const InputElement: React.FC<InputElementProps & TextFieldProps> = ({
  name,
  control,
  rules,
  required = false,
  ...textFieldProps // Spread all other TextField props
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field,
        fieldState: { error },
      }: {
        field: any;
        fieldState: { error?: FieldError };
      }) => (
        <BeautifulTextField
          {...field}
          fullWidth
          size="small"
          margin="dense"
          required={required}
          error={!!error}
          helperText={error ? error.message : ''}
          InputLabelProps={{
            shrink: !!field.value || undefined, // Force label to shrink if value exists
          }}
          {...textFieldProps} // Apply all other TextField props
        />
      )}
    />
  );
};

export default InputElement;
